// ชื่อที่ใช้ "ชี้ตัวของ" บนหน้าจอพนักงาน — อังกฤษก่อนเสมอ
//
// ที่มา (26 ต.ค. 2569): หน้า สินค้า & HS เรียงการ์ด 12 ใบติดกันแล้วอ่านว่า "หมวกแก็ป" เหมือนกันหมด
//   แยกไม่ออกว่าใบไหนคือใบไหน ต้องไล่อ่านรหัสทีละใบ
//
// สาเหตุไม่ใช่บั๊ก — `name_th` คือ **ชื่อที่ใช้สำแดงศุลกากร** ซึ่งจงใจเขียนให้กว้างและเป็นคำสามัญ
//   (หมวกทุกทรงทุกสี = "หมวกแก็ป") ส่วน `name_en` คือชื่อรุ่นจริงที่ตรงกับ invoice และแค็ตตาล็อกแบรนด์
//   ⇒ งาน "หาของ" ใช้ชื่ออังกฤษ · งาน "สำแดง" ใช้ชื่อไทย คนละหน้าที่กัน
//
// 🚨 ห้ามเอาไปใช้กับของที่ **เขียนลงฐานข้อมูล** หรือ **พิมพ์ส่งหน่วยงาน** — ตรงนั้นชื่อไทยคือของจริง:
//   · `line_item.description` (ชื่อที่ขึ้นบนเอกสารส่ง broker) — Modals.jsx submit · data.js importInvoiceLines
//   · แผ่นขอใบอนุญาต สมอ./เกษตร (PermitSheet "ชื่อสินค้า (ไทย)")
//   · สรุปใน zip ที่ยื่นราชการ (data.js buildPermitZip)
//   · ด่านความครบของใบข้อมูลสินค้า (`cardGaps` ยังต้องบังคับให้มีชื่อไทย)
//   ใบ dossier A4 ทำถูกอยู่แล้ว: ชื่ออังกฤษเป็นหัว แล้วมีบรรทัด "ไทย: …" ต่อท้าย
//
// 🚨 28 ต.ค. 69 user: "เอาชื่อไทยของสินค้าคืนมา … ชื่อภาษาไทยยังต้องมีอยู่ ต้องใช้ในเอกสารให้ศุลกากร อย่าเอาออกไป"
//   รอบแรกโชว์ชื่อไทยแค่ตอนเอาเมาส์ชี้ (itemTitle) = สำหรับคนใช้งานคือ "ชื่อไทยหายไป"
//   ⇒ ทุกที่ที่โชว์ชื่อ ต้องมีบรรทัด "ไทย: …" ใต้ชื่อเสมอ — ใช้ <NameTh/> (components/NameTh.jsx)
//   ใบข้อมูลสินค้าที่ยังไม่มีชื่อไทย ให้ขึ้น "ยังไม่มีชื่อไทย" (ศุลกากรต้องใช้) ไม่ใช่เว้นว่างเงียบ ๆ

const t = (v) => String(v ?? '').trim();

// ชื่อดิบ — ไม่มีชื่อเลยคืนค่าว่าง ใช้ตอน "เอาไปคำนวณ" (เช่นเทียบรุ่นใน siblings.js)
// ⚠ อย่าใช้ itemName() แทนในงานคำนวณ: มันคืน '—' เมื่อไม่มีชื่อ ซึ่งของสองชิ้นที่ไม่มีชื่อ
//   จะกลายเป็น "ชื่อเหมือนกัน 100%" แล้วถูกจับเป็นรุ่นเดียวกัน
export function itemNameRaw(c) {
  if (!c) return '';
  return t(c.name_en) || t(c.name_th) || t(c.sku);
}

// ใช้กับใบข้อมูลสินค้า (product_classification) และแถวจากทะเบียนสินค้า (pd_products)
export function itemName(c, fallback = '') {
  return itemNameRaw(c) || t(fallback) || '—';
}

// ข้อความ hover — เห็นทั้งสองชื่อโดยไม่ต้องกดเข้าไปดู (ชื่อไทยยังต้องตรวจว่ากรอกหรือยัง)
export function itemTitle(c) {
  if (!c) return undefined;
  const en = t(c.name_en), th = t(c.name_th);
  if (en && th && en !== th) return hasThai(th) ? `${en}\nไทย: ${th}` : `${en}\n(ช่องชื่อไทยยังเป็นภาษาอังกฤษ: ${th})`;
  return en || th || undefined;
}

// ⚠ 28 ต.ค. 69 ช่อง name_th 2,708/3,324 ใบ "ไม่ใช่ภาษาไทย" — 21 ก.ค. รอบเอาชื่อจากทะเบียน PD มาทับ
//   (ช่องไทยของ PD เก็บชื่ออังกฤษ) ทับชื่อจากทะเบียนศุลกากร C76 ไปหมด
//   ✅ 29 ต.ค. คืนแล้วทั้ง 3,324 ใบ (db/data-fix-2026-10-29-restore-name-th.sql) — แต่ยังกันไว้:
//   โชว์ "ไทย: …" เฉพาะที่เป็นภาษาไทยจริง · ด่านความครบ (db.cardGaps) และหน้าแก้ไขสินค้าไม่รับชื่ออังกฤษในช่องนี้
export const hasThai = (v) => /[\u0E00-\u0E7F]/.test(String(v ?? ''));

// ชื่อไทยสำหรับบรรทัดที่สองใต้ชื่อ — ว่างถ้าไม่มี / ไม่ใช่ภาษาไทย / ซ้ำกับชื่อที่โชว์อยู่แล้ว
export function itemNameTh(c) {
  if (!c) return '';
  const th = t(c.name_th);
  return th && hasThai(th) && th !== itemNameRaw(c) ? th : '';
}

// สถานะชื่อไทยของใบข้อมูลสินค้า: 'ok' · 'english' (มีค่าแต่ไม่ใช่ภาษาไทย) · 'empty'
export function thaiNameState(c) {
  const th = t(c?.name_th);
  return !th ? 'empty' : hasThai(th) ? 'ok' : 'english';
}
